/**
 * FormSection — the card around a single editable form item.
 *
 * Built on the shadcn Form primitives, so it must be rendered inside a
 * `<FormField>`: label association, `aria-invalid`, `aria-describedby`, and the
 * validation message are all handled by `FormLabel`/`FormControl`/`FormMessage` —
 * callers just pass the label and the control. Read-only fields (account details,
 * derived values) are still RHF fields; they render a read-only control as children.
 */
import * as React from 'react';

import { FormControl, FormDescription, FormItem, FormLabel, FormMessage } from '@/_shared/components/ui/form';
import { cn } from '@/_shared/lib/utils';

export interface FormSectionProps {
  label: React.ReactNode;
  /** Show a muted "(optional)" marker beside the label. */
  optional?: boolean;
  /** Help text rendered as a subtitle below the label. */
  description?: React.ReactNode;
  /** The control — a single element (Input/Textarea) wrapped in FormControl. */
  children: React.ReactNode;
  className?: string;
  /**
   * Suppresses FormSection's own `<FormControl>` wrapper around `children`. Defaults to `false` —
   * every existing caller renders byte-identically unless it opts in.
   *
   * `FormControl` is a Radix `Slot`: it clones `id`/`aria-describedby`/`aria-invalid` onto its single
   * child. That works for `children` that render one real DOM node directly (`Input`, `Textarea`), but
   * a Radix `Select` root renders no DOM node of its own — wrapping the whole `<Select>` block would
   * strand those attributes off any real element instead of landing on the interactive
   * `SelectTrigger`, breaking the label association. Set `omitFormControl` when `children` needs
   * `FormControl` placed further down, around the actual interactive element (a `Select`'s
   * `SelectTrigger`, per `DatabricksForm.tsx` / `RedshiftForm.tsx`) — FormSection still supplies the
   * card, label, description and message.
   */
  omitFormControl?: boolean;
}

export function FormSection({
  label,
  optional,
  description,
  children,
  className,
  omitFormControl = false,
}: FormSectionProps): React.JSX.Element {
  return (
    // A subtle grey-25 fill with a grey-50 border — one card per form item.
    <FormItem className={cn('rounded-xl border border-grey-50 bg-grey-25 p-4', className)}>
      <FormLabel className="px-1">
        {label}
        {optional ? <span className="text-xs font-normal text-grey-400">(optional)</span> : null}
      </FormLabel>
      {description ? <FormDescription className="mb-2 px-1">{description}</FormDescription> : null}
      {omitFormControl ? children : <FormControl>{children}</FormControl>}
      <FormMessage />
    </FormItem>
  );
}
